import type { GrantablePermission } from '@palaccount/contracts';
import type { Grid } from '@palaccount/spreadsheet';
import { ClipboardCopy, ClipboardPaste, Download, FileUp } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '@/api';
import type { DocumentToolbarAction } from '@/components/document-toolbar';
import { readImportFile } from '@/features/admin/import-file';
import { downloadXlsx } from '@/features/reports/export';

import {
  linesToTsv,
  parseImportedRows,
  parseTsv,
  type CopyableLine,
  resolveImportedLines,
  TEMPLATE_HEADINGS,
  type ImportRowError,
  type ResolvedItem,
  type ResolvedLine,
} from './line-import';

/**
 * Wave 4C — the line tools every document grid shares: "Import lines" and
 * "Download template".
 *
 * WHY A HOOK THAT RETURNS ACTS, AND NOT A TOOLBAR NODE. The editors' toolbar
 * is a declaration (`DocumentToolbar`'s `actions`), and its one escape hatch —
 * a `node` act — is pinned by `document-toolbar-roster.test.tsx` to the Print
 * pair. So the acts arrive here as ordinary button acts the screen spreads
 * into its array, and `LineImportActs` below is the part that is not a button:
 * the hidden file chooser and the sentence the last import left behind.
 */

/** Which side of the business the grid is on: it decides the default price. */
export type LineSide = 'sales' | 'purchase';

/** An item as the code lookup returns it — the list route's row, trimmed. */
export interface LookedUpItem extends ResolvedItem {
  code: string;
  nameAr: string | null;
  costPrice?: string | undefined;
  stockUnitSymbol: string;
  stockUnitPrecision: number;
  /** The item's tier prices (R5), for a sales grid that prices by the customer's tier. */
  prices: Array<{ tierId: string; price: string }>;
}

/**
 * The item with exactly this code, through the items list route's `q`
 * (a contains-search, so the exact match is picked out of the page).
 * A purchase grid prices at the item's cost when it has one — the same
 * default its own item pick fills.
 */
export async function findItemByCode(code: string, side: LineSide): Promise<LookedUpItem | null> {
  const response = await api.items.list({ query: { q: code, status: 'active', pageSize: 20 } });
  if (response.status !== 200) return null;
  const hit = response.body.items.find((item) => item.code.toLowerCase() === code.toLowerCase());
  if (!hit) return null;
  const cost = hit.costPrice && hit.costPrice !== '0.00' ? hit.costPrice : null;
  return {
    id: hit.id,
    code: hit.code,
    name: hit.name,
    nameAr: hit.nameAr,
    costPrice: hit.costPrice,
    stockUnitSymbol: hit.stockUnitSymbol,
    stockUnitPrecision: hit.stockUnitPrecision,
    prices: hit.prices,
    unitPrice: side === 'purchase' ? (cost ?? hit.unitPrice ?? '') : hit.unitPrice,
  };
}

export interface LineImportOptions {
  /** Stable per screen; the hidden input's test id is `${id}-import-file`. */
  id: string;
  side: LineSide;
  /** The grid cannot hold a line without an item (see `ResolveOptions`). */
  requireItem?: boolean;
  /** The document's question: a draft, editable. */
  visible: boolean;
  /** The role's question — greys the import, never the template. */
  gate?: { permission: GrantablePermission; reasonKey: string };
  disabled?: boolean;
  /** The resolved lines, to be appended after whatever is already typed. */
  onLines: (lines: ResolvedLine<LookedUpItem>[]) => void;
  /**
   * Wave 4C's LineClipboard — the grid's lines as "Copy lines" writes them
   * (code, description, qty, unit price, discount). Copy is a read and is
   * offered on any document that has lines; Paste follows Import's rules.
   */
  copyLines?: () => CopyableLine[];
  /** Overrides the default price of a matched item (wave 4C: the customer's price tier). */
  price?: (item: LookedUpItem) => string;
  /** Fills a blank description from the item (wave 4C's AR/EN toggle). */
  describe?: (item: LookedUpItem) => string;
}

export interface LineImportState {
  acts: DocumentToolbarAction[];
  /** Hidden file chooser + the last import's report; render once on the screen. */
  panel: ReactNode;
}

/** The template's file: the heading row only, in English, the sheet named in the reader's language. */
export function downloadLineTemplate(sheetName: string, rightToLeft: boolean): void {
  downloadXlsx('lines-template.xlsx', sheetName, TEMPLATE_HEADINGS, [], {}, rightToLeft);
}

export function useLineImport(options: LineImportOptions): LineImportState {
  const { t, i18n } = useTranslation();
  const input = useRef<HTMLInputElement | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [errors, setErrors] = useState<ImportRowError[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** File or clipboard: the same rows, the same resolver, the same report. */
  const ingest = async (read: () => Promise<Grid>, emptyKey: 'file' | 'clipboard') => {
    setNote(null);
    setErrors([]);
    setFailure(null);
    setBusy(true);
    try {
      const parsed = parseImportedRows(await read());
      if (parsed.length === 0) {
        setNote(emptyKey === 'file' ? t('lines.importEmpty') : t('lines.clipboardEmpty'));
        return;
      }
      const find = async (code: string) => {
        const item = await findItemByCode(code, options.side);
        return item && options.price ? { ...item, unitPrice: options.price(item) } : item;
      };
      const summary = await resolveImportedLines(parsed, find, {
        ...(options.requireItem ? { requireItem: true } : {}),
        ...(options.describe ? { describe: options.describe } : {}),
      });
      if (summary.lines.length > 0) options.onLines(summary.lines);
      setErrors(summary.errors);
      setNote(
        t('lines.importSummary', {
          lines: summary.lines.length,
          matched: summary.matched,
          problems: summary.errors.length,
        }),
      );
    } catch (thrown) {
      setFailure(thrown instanceof Error ? thrown.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const onFile = (file: File) => ingest(() => readImportFile(file, t), 'file');

  const onPaste = () =>
    ingest(async () => {
      let text: string;
      try {
        text = await navigator.clipboard.readText();
      } catch {
        // A refused permission, or a browser with no async clipboard.
        throw new Error(t('lines.clipboardDenied'));
      }
      return parseTsv(text);
    }, 'clipboard');

  const onCopy = async () => {
    const lines = options.copyLines?.() ?? [];
    setNote(null);
    setErrors([]);
    setFailure(null);
    try {
      await navigator.clipboard.writeText(linesToTsv(lines));
      setNote(t('lines.copied', { lines: lines.length }));
    } catch {
      setFailure(t('lines.clipboardDenied'));
    }
  };

  const rtl = i18n.language.startsWith('ar');
  const acts: DocumentToolbarAction[] = [
    {
      // PART 4.6's Excel row, on the editor: lines from a small .xlsx/.csv,
      // resolved by item code. The act opens the chooser; the input reads.
      id: 'importLines',
      row: 'excel',
      label: t('lines.import'),
      icon: <FileUp aria-hidden />,
      variant: 'outline',
      visible: options.visible,
      ...(options.gate ? { gate: options.gate } : {}),
      disabled: options.disabled === true,
      busy,
      onAct: () => input.current?.click(),
    },
    {
      // The columns the import reads, as an empty sheet to fill in.
      id: 'linesTemplate',
      row: 'excel',
      label: t('lines.template'),
      icon: <Download aria-hidden />,
      variant: 'outline',
      visible: options.visible,
      onAct: () => downloadLineTemplate(t('lines.templateSheet'), rtl),
    },
    ...(options.copyLines
      ? ([
          {
            // A read: offered whenever the grid has lines, posted or not.
            id: 'copyLines',
            row: null,
            label: t('lines.copy'),
            icon: <ClipboardCopy aria-hidden />,
            variant: 'outline',
            onAct: () => void onCopy(),
          },
          {
            // A write, with Import's own visibility and gate.
            id: 'pasteLines',
            row: null,
            label: t('lines.paste'),
            icon: <ClipboardPaste aria-hidden />,
            variant: 'outline',
            visible: options.visible,
            ...(options.gate ? { gate: options.gate } : {}),
            disabled: options.disabled === true || busy,
            onAct: () => void onPaste(),
          },
        ] satisfies DocumentToolbarAction[])
      : []),
  ];

  const panel = (
    <LineImportActs
      id={options.id}
      inputRef={input}
      onFile={(file) => void onFile(file)}
      note={note}
      errors={errors}
      failure={failure}
    />
  );
  return { acts, panel };
}

/**
 * The part of the import that is not a toolbar button: the hidden chooser,
 * the one-sentence summary, and the per-row list of codes that matched no
 * item (never a silent drop).
 */
export function LineImportActs({
  id,
  inputRef,
  onFile,
  note,
  errors,
  failure,
}: {
  id: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  onFile: (file: File) => void;
  note: string | null;
  errors: readonly ImportRowError[];
  failure: string | null;
}) {
  const { t } = useTranslation();
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.csv,.txt"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        data-testid={`${id}-import-file`}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onFile(file);
        }}
      />
      {failure ? (
        <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {failure}
        </p>
      ) : null}
      {note ? (
        <div role="status" className="grid gap-1 text-sm text-muted-foreground">
          <p>{note}</p>
          {errors.length > 0 ? (
            <ul aria-label={t('lines.importProblems')} className="list-disc ps-5 text-amber-800 dark:text-amber-300">
              {errors.map((error) => (
                <li key={`${error.row}-${error.code}`}>
                  {error.reason === 'noCode'
                    ? t('lines.errNoCode', { row: error.row })
                    : t('lines.errUnknownCode', { row: error.row, code: error.code })}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
